import {
  type AttachmentCapabilities,
  type AttachmentFile,
  type AttachmentTarget,
  type AttachmentTypeCode,
  extractErrorMessage,
  useDeleteAttachmentMutation,
  useGetAttachmentBundlesQuery,
  useUpdateAttachmentMutation,
  useUploadAttachmentsMutation,
} from "@ots-frontend/ots-data";
import { useCallback } from "react";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import type { AttachmentManagerProps } from "../AttachmentManager";
import { AttachmentManagerDialog } from "../AttachmentManagerDialog";

/** Every target's attachments after a change, for hosts that keep their own copy (WaterGo grids). */
export type AttachmentSnapshot = AttachmentTarget & { attachments: AttachmentFile[] };

export type UseConnectedAttachmentManagerOptions = {
  targets: AttachmentTarget[];
  capabilities?: Partial<AttachmentCapabilities>;
  attachmentTypes?: AttachmentTypeCode[];
  title?: string;
  /** Skips loading, e.g. while the dialog is closed. */
  skip?: boolean;
  /** After an upload, edit, or delete, with every target's attachments as they now are. */
  onChanged?: (snapshots: AttachmentSnapshot[]) => void;
};

/**
 * The attachment manager's props wired to the attachment service: the targets' bundles, and upload, edit,
 * and delete. Shared by the dashboard bridge (WaterGo) and React hosts such as Driver Activity.
 */
export const useConnectedAttachmentManager = ({
  targets,
  capabilities,
  attachmentTypes,
  title,
  skip = false,
  onChanged,
}: UseConnectedAttachmentManagerOptions): AttachmentManagerProps => {
  const { data, error, isFetching, refetch } = useGetAttachmentBundlesQuery(targets, { skip: skip || targets.length === 0 });
  const [uploadAttachments] = useUploadAttachmentsMutation();
  const [updateAttachment] = useUpdateAttachmentMutation();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const publish = useCallback(async () => {
    const refreshed = await refetch();
    if (!refreshed.data || !onChanged) return;
    onChanged(refreshed.data.map(({ target, bundle }) => ({ ...target, attachments: bundle.attachments })));
  }, [onChanged, refetch]);

  return {
    attachments: data?.flatMap(({ bundle }) => bundle.attachments) ?? [],
    targets,
    capabilities: data?.find(({ bundle }) => bundle.capabilities)?.bundle.capabilities ?? capabilities,
    attachmentTypes,
    loading: isFetching,
    error: error ? extractErrorMessage(error, "Attachments could not be loaded.") : undefined,
    title,
    bulkDownloadUrl: data?.length === 1 ? data[0].bundle.bulkDownloadUrl : undefined,
    onRefresh: async () => { await refetch(); },
    onUpload: async (items) => {
      await uploadAttachments(items).unwrap();
      await publish();
    },
    onUpdate: async (attachment, changes) => {
      await updateAttachment({ attachment, changes }).unwrap();
      await publish();
    },
    onDelete: async (attachment) => {
      await deleteAttachment(attachment).unwrap();
      await publish();
    },
  };
};

export type ConnectedAttachmentManagerDialogProps = Omit<UseConnectedAttachmentManagerOptions, "skip"> & {
  open: boolean;
  onClose: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The attachment manager dialog with its data fetched and saved through the attachment service. */
export const ConnectedAttachmentManagerDialog = ({ open, onClose, brand, colorScheme, ...options }: ConnectedAttachmentManagerDialogProps) => {
  const manager = useConnectedAttachmentManager({ ...options, skip: !open });
  return <AttachmentManagerDialog {...manager} open={open} onClose={onClose} brand={brand} colorScheme={colorScheme}/>;
};
